// Bundle UI `inspection-v1` — catálogo da ficha (espelha `templateVersion` do back).
//
// Fonte normativa: docs/prd/inspection/inspection-form.md §7.3 e
// docs/prd/inspection/inspection-endpoints.md §7.1 (`itemKey` / `answerValue`).
//
// Rótulos visíveis são chaves `inspection.catalog.v1.*` (i18n do front). O texto
// PT-BR normativo vive em `shared/i18n/translations/inspection/catalog/`.
// `localizeInspectionStep` resolve no idioma da UI.

import type {
  InspectionAnswerOption,
  InspectionFieldDef,
  InspectionItemDef,
  InspectionStepDef,
} from "./inspection-form.types";

// ---------------------------------------------------------------------------
// Conjuntos de respostas reutilizados
// ---------------------------------------------------------------------------

const GRADE_OPTIONS: InspectionAnswerOption[] = [
  { value: "good", label: "inspection.catalog.v1.common.good" },
  { value: "regular", label: "inspection.catalog.v1.common.regular", anomaly: true },
  { value: "deficient", label: "inspection.catalog.v1.common.deficient", anomaly: true },
];

const GRADE_OPTIONS_WITH_NA: InspectionAnswerOption[] = [
  ...GRADE_OPTIONS,
  { value: "notApplicable", label: "inspection.catalog.v1.common.na", dismisses: true },
];

const YES_NO_ANOMALY_ON_YES: InspectionAnswerOption[] = [
  { value: "yes", label: "inspection.catalog.v1.common.yes", anomaly: true },
  { value: "no", label: "inspection.catalog.v1.common.no" },
];

const YES_NO_ANOMALY_ON_NO: InspectionAnswerOption[] = [
  { value: "yes", label: "inspection.catalog.v1.common.yes" },
  { value: "no", label: "inspection.catalog.v1.common.no", anomaly: true },
];

const YES_NO_PLAIN: InspectionAnswerOption[] = [
  { value: "yes", label: "inspection.catalog.v1.common.yes" },
  { value: "no", label: "inspection.catalog.v1.common.no" },
];

const YES_NO_NA_PLAIN: InspectionAnswerOption[] = [
  ...YES_NO_PLAIN,
  { value: "notApplicable", label: "inspection.catalog.v1.common.na" },
];

const YES_NO_NA_ANOMALY_ON_YES: InspectionAnswerOption[] = [
  { value: "yes", label: "inspection.catalog.v1.common.yes", anomaly: true },
  { value: "no", label: "inspection.catalog.v1.common.no" },
  { value: "notApplicable", label: "inspection.catalog.v1.common.na" },
];

/** Gate de step (form §7.3): dispensa o step inteiro. */
const STEP_GATE_OPTIONS: InspectionAnswerOption[] = [
  { value: "yes", label: "inspection.catalog.v1.common.yes" },
  { value: "notApplicable", label: "inspection.catalog.v1.common.notApplicable" },
];

/** Tipo de extravasor — mesmo enum da Hidrologia do Admin. */
export const SPILLWAY_TYPE_OPTIONS = [
  { value: "surface", i18nKey: "admin.structures.hydrology.spillwayType.surface" },
  { value: "tulip", i18nKey: "admin.structures.hydrology.spillwayType.tulip" },
  {
    value: "lateralChannel",
    i18nKey: "admin.structures.hydrology.spillwayType.lateralChannel",
  },
  { value: "labyrinth", i18nKey: "admin.structures.hydrology.spillwayType.labyrinth" },
  { value: "gated", i18nKey: "admin.structures.hydrology.spillwayType.gated" },
  { value: "bottom", i18nKey: "admin.structures.hydrology.spillwayType.bottom" },
] as const;

const OBSERVATIONS_LABEL = "inspection.catalog.v1.common.observations";
const PHOTOS_LABEL = "inspection.catalog.v1.common.photos";
const OTHERS_LABEL = "inspection.catalog.v1.common.others";
const LOCAL_FIELD: InspectionFieldDef = {
  key: "location",
  label: "inspection.catalog.v1.common.local",
  type: "text",
  required: true,
  placeholder: "inspection.catalog.v1.placeholders.local",
};

/** Item "Outros (identificar)" — descritivo, sempre opcional, nunca anomalia. */
const buildOthersItem = (
  itemKey: string,
  visibleWhen?: InspectionItemDef["visibleWhen"],
): InspectionItemDef => ({
  itemKey,
  label: OTHERS_LABEL,
  descriptive: true,
  optional: true,
  infoHints: ["optional"],
  admitsEvidence: true,
  visibleWhen,
  fields: [
    {
      key: "description",
      label: "inspection.catalog.v1.common.describe",
      type: "text",
      placeholder: "inspection.catalog.v1.placeholders.othersDescribe",
    },
  ],
  observations: { mode: "always", required: false },
  observationsPlaceholder: "inspection.catalog.v1.placeholders.othersObservations",
  photos: { mode: "always", required: false },
});

const gradeItem = (
  itemKey: string,
  label: string,
  overrides: Partial<InspectionItemDef> = {},
): InspectionItemDef => ({
  itemKey,
  label,
  options: GRADE_OPTIONS,
  observations: { mode: "anomaly" },
  photos: { mode: "anomaly" },
  admitsEvidence: true,
  ...overrides,
});

// ---------------------------------------------------------------------------
// Step: Acessos (form §7.3.2)
// ---------------------------------------------------------------------------

const ACCESSES_STEP: InspectionStepDef = {
  stepKey: "accesses",
  title: "inspection.catalog.v1.stepShort.accesses",
  items: [
    gradeItem("accesses.generalConservation", "inspection.catalog.v1.items.accesses.generalConservation"),
    gradeItem("accesses.slopes", "inspection.catalog.v1.items.accesses.slopes"),
    gradeItem("accesses.floorPavement", "inspection.catalog.v1.items.accesses.floorPavement"),
    gradeItem("accesses.drainageDevices", "inspection.catalog.v1.items.accesses.drainageDevices"),
  ],
};

// ---------------------------------------------------------------------------
// Step: Maciço / Corpo da barragem (form §7.3.4)
// ---------------------------------------------------------------------------

const DAM_BODY_STEP: InspectionStepDef = {
  stepKey: "damBody",
  allowPartialSave: true,
  title: "inspection.catalog.v1.stepShort.damBody",
  items: [
    {
      itemKey: "damBody.cracksIdentified",
      label: "inspection.catalog.v1.items.damBody.cracksIdentified",
      options: YES_NO_PLAIN,
      observations: { mode: "never" },
      photos: { mode: "never" },
      occurrences: {
        when: ["yes"],
        prefix: "T",
        addLabel: "inspection.catalog.v1.common.addCrack",
        rowTestId: "crack-occurrence-row",
        isAnomaly: true,
        requireAtLeastOne: true,
        showKey: true,
        fields: [
          {
            key: "lengthM",
            label: "inspection.catalog.v1.common.lengthM",
            type: "text",
            required: true,
            placeholder: "inspection.catalog.v1.placeholders.lengthExample",
          },
          {
            key: "openingMm",
            label: "inspection.catalog.v1.common.openingMm",
            type: "text",
            required: true,
            placeholder: "inspection.catalog.v1.placeholders.openingExample",
          },
          {
            key: "orientation",
            label: "inspection.catalog.v1.common.orientation",
            type: "select",
            required: true,
            options: [
              { value: "longitudinal", label: "inspection.catalog.v1.choices.longitudinal" },
              { value: "transversal", label: "inspection.catalog.v1.choices.transversal" },
            ],
          },
        ],
        emptyMessageKey: "inspection.form.occurrenceEmptyCrack",
      },
    },
    {
      itemKey: "damBody.settlementsIdentified",
      label: "inspection.catalog.v1.items.damBody.settlementsIdentified",
      options: YES_NO_PLAIN,
      observations: { mode: "never" },
      photos: { mode: "never" },
      occurrences: {
        when: ["yes"],
        prefix: "R",
        addLabel: "inspection.catalog.v1.common.addSettlement",
        rowTestId: "settlement-occurrence-row",
        isAnomaly: true,
        requireAtLeastOne: true,
        entryTag: "fields",
        tagFields: ["location", "manifestation"],
        fields: [
          {
            key: "location",
            label: "inspection.catalog.v1.common.local",
            type: "select",
            required: true,
            options: [
              { value: "Crista", label: "inspection.catalog.v1.choices.crest" },
              { value: "Bermas", label: "inspection.catalog.v1.choices.berms" },
              { value: "Taludes", label: "inspection.catalog.v1.choices.slopes" },
            ],
          },
          {
            key: "manifestation",
            label: "inspection.catalog.v1.common.manifestation",
            type: "select",
            required: true,
            options: [
              { value: "depression", label: "inspection.catalog.v1.choices.depression" },
              { value: "settlement", label: "inspection.catalog.v1.choices.settlement" },
            ],
          },
        ],
        emptyMessageKey: "inspection.form.occurrenceEmptySettlement",
      },
    },
    {
      itemKey: "damBody.seepagesIdentified",
      label: "inspection.catalog.v1.items.damBody.seepagesIdentified",
      options: YES_NO_PLAIN,
      observations: { mode: "never" },
      photos: { mode: "never" },
      occurrences: {
        when: ["yes"],
        prefix: "S",
        addLabel: "inspection.catalog.v1.common.addSeepage",
        rowTestId: "seepage-occurrence-row",
        isAnomaly: true,
        requireAtLeastOne: true,
        showKey: true,
        fields: [
          LOCAL_FIELD,
          {
            key: "solidCarryover",
            label: "inspection.catalog.v1.common.solidCarryover",
            type: "radio",
            required: true,
            options: [
              { value: "yes", label: "inspection.catalog.v1.common.yes" },
              { value: "no", label: "inspection.catalog.v1.common.no" },
            ],
          },
        ],
        emptyMessageKey: "inspection.form.occurrenceEmptySeepage",
      },
    },
    {
      itemKey: "damBody.surfaceErosions",
      label: "inspection.catalog.v1.items.damBody.surfaceErosions",
      options: YES_NO_PLAIN,
      observations: { mode: "never" },
      photos: { mode: "never" },
      occurrences: {
        when: ["yes"],
        prefix: "E",
        addLabel: "inspection.catalog.v1.common.addErosion",
        rowTestId: "erosion-occurrence-row",
        isAnomaly: true,
        requireAtLeastOne: true,
        showKey: true,
        fields: [LOCAL_FIELD],
        emptyMessageKey: "inspection.form.occurrenceEmptyErosion",
      },
    },
    {
      itemKey: "damBody.surfaceDrainage",
      label: "inspection.catalog.v1.items.damBody.surfaceDrainage",
      options: STEP_GATE_OPTIONS,
      observations: { mode: "never" },
      photos: { mode: "never" },
      nestedWhen: ["yes"],
      nested: [
        {
          itemKey: "damBody.surfaceDrainage.cleaningState",
          label: "inspection.catalog.v1.common.cleaningState",
          options: GRADE_OPTIONS,
          observations: { mode: "anomaly" },
          photos: { mode: "anomaly" },
          admitsEvidence: true,
        },
        {
          itemKey: "damBody.surfaceDrainage.structuralCondition",
          label: "inspection.catalog.v1.common.structuralConditions",
          options: GRADE_OPTIONS,
          observations: { mode: "anomaly" },
          photos: { mode: "anomaly" },
          admitsEvidence: true,
        },
      ],
    },
    {
      itemKey: "damBody.drainageChannel",
      label: "inspection.catalog.v1.items.damBody.drainageChannel",
      options: STEP_GATE_OPTIONS,
      observations: { mode: "never" },
      photos: { mode: "never" },
      occurrences: {
        when: ["yes"],
        prefix: "L",
        addLabel: "inspection.catalog.v1.common.addLine",
        rowTestId: "drainage-channel-row",
        isAnomaly: false,
        requireAtLeastOne: true,
        entryTag: "line",
        fields: [LOCAL_FIELD],
        grades: [
          // `gradeFieldKey` usa o último segmento → `cleaningState` /
          // `structuralCondition` no `answerValue` da ocorrência (endpoints §7.1).
          gradeItem(
            "damBody.drainageChannel.cleaningState",
            "inspection.catalog.v1.common.cleaningState",
          ),
          gradeItem(
            "damBody.drainageChannel.structuralCondition",
            "inspection.catalog.v1.common.structuralCondition",
          ),
        ],
        observations: { mode: "anomaly" },
        photos: { mode: "anomaly" },
        emptyMessageKey: "inspection.form.occurrenceEmptyChannel",
      },
    },
    {
      itemKey: "damBody.vegetalCover",
      label: "inspection.catalog.v1.items.damBody.vegetalCover",
      options: [
        {
          value: "grassNeedsPruning",
          label: "inspection.catalog.v1.choices.grassNeedsPruning",
          anomaly: true,
        },
        {
          value: "controlledOrAbsentDuringRaising",
          label: "inspection.catalog.v1.choices.controlledOrAbsentDuringRaising",
        },
        {
          value: "shrubOrTreeVegetation",
          label: "inspection.catalog.v1.choices.shrubOrTreeVegetation",
          anomaly: true,
        },
        {
          value: "absentWithoutRaising",
          label: "inspection.catalog.v1.choices.absentWithoutRaising",
          anomaly: true,
        },
      ],
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.pests.termitesRodents",
      sectionTitle: "inspection.catalog.v1.sections.pests",
      label: "inspection.catalog.v1.items.damBody.pests.termitesRodents",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.pests.animals",
      label: "inspection.catalog.v1.items.damBody.pests.animals",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.pests.burrows",
      label: "inspection.catalog.v1.items.damBody.pests.burrows",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.outletSilting",
      sectionTitle: "inspection.catalog.v1.sections.internalDrainage",
      label: "inspection.catalog.v1.items.damBody.internalDrainage.outletSilting",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.solidCarryover",
      label: "inspection.catalog.v1.common.solidCarryover",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.flowChange",
      label: "inspection.catalog.v1.items.damBody.internalDrainage.flowChange",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.drainSaturation",
      label: "inspection.catalog.v1.items.damBody.internalDrainage.drainSaturation",
      options: YES_NO_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.flowMeterOperational",
      label: "inspection.catalog.v1.items.damBody.internalDrainage.flowMeterOperational",
      options: YES_NO_ANOMALY_ON_NO,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.internalDrainage.flowMeasured",
      label: "inspection.catalog.v1.items.damBody.internalDrainage.flowMeasured",
      options: YES_NO_PLAIN,
      fields: [
        {
          key: "flowValue",
          label: "inspection.catalog.v1.common.value",
          type: "text",
          required: true,
          when: ["yes"],
          placeholder: "inspection.catalog.v1.placeholders.flowValue",
        },
      ],
      observations: { mode: "never" },
      photos: { mode: "answers", values: ["yes"], required: true },
      admitsEvidence: true,
    },
    {
      itemKey: "damBody.complementaryStructures",
      label: "inspection.catalog.v1.items.damBody.complementaryStructures",
      options: STEP_GATE_OPTIONS,
      observations: { mode: "never" },
      photos: { mode: "never" },
      nestedWhen: ["yes"],
      nested: [
        [
          "damBody.complementaryStructures.reinforcement",
          "inspection.catalog.v1.items.damBody.reinforcement",
        ],
        ["damBody.complementaryStructures.saddleDike", "inspection.catalog.v1.items.damBody.saddleDike"],
        ["damBody.complementaryStructures.sump", "inspection.catalog.v1.items.damBody.sump"],
      ].map(
        ([itemKey, label]): InspectionItemDef => ({
          itemKey: itemKey!,
          label: label!,
          options: GRADE_OPTIONS_WITH_NA,
          naCheckbox: true,
          fields: [
            {
              key: "elevationM",
              label: "inspection.catalog.v1.common.elevationM",
              type: "text",
              required: "whenValue",
              placeholder: "inspection.catalog.v1.placeholders.elevation",
            },
          ],
          observations: { mode: "anomaly" },
          photos: { mode: "anomaly" },
          admitsEvidence: true,
        }),
      ),
    },
  ],
};

// ---------------------------------------------------------------------------
// Step: Reservatório (form §7.3.5)
// ---------------------------------------------------------------------------

const RESERVOIR_STEP: InspectionStepDef = {
  stepKey: "reservoir",
  title: "inspection.catalog.v1.steps.reservoir",
  gate: {
    itemKey: "reservoir.applies",
    label: "inspection.catalog.v1.steps.reservoir",
    options: STEP_GATE_OPTIONS,
    observations: { mode: "never" },
    photos: { mode: "never" },
  },
  items: [
    {
      itemKey: "reservoir.bankCondition",
      label: "inspection.catalog.v1.items.reservoir.bankCondition",
      options: YES_NO_PLAIN,
      nestedWhen: ["yes"],
      nested: [
        {
          itemKey: "reservoir.bankCondition.grade",
          label: "inspection.catalog.v1.common.note",
          options: GRADE_OPTIONS,
          observations: { mode: "anomaly" },
          photos: { mode: "anomaly" },
          admitsEvidence: true,
        },
      ],
      observations: { mode: "never" },
      photos: { mode: "never" },
    },
    {
      itemKey: "reservoir.impermeabilized",
      label: "inspection.catalog.v1.items.reservoir.impermeabilized",
      options: YES_NO_PLAIN,
      nestedWhen: ["yes"],
      nested: [
        {
          itemKey: "reservoir.geomembraneConservation",
          label: "inspection.catalog.v1.items.reservoir.geomembraneConservation",
          options: GRADE_OPTIONS,
          observations: { mode: "never" },
          photos: { mode: "never" },
          occurrences: {
            when: ["regular", "deficient"],
            prefix: "G",
            addLabel: "inspection.catalog.v1.common.addOccurrence",
            rowTestId: "geomembrane-occurrence-row",
            isAnomaly: true,
            requireAtLeastOne: true,
            showKey: true,
            fields: [LOCAL_FIELD],
            emptyMessageKey: "inspection.form.occurrenceEmptyGeomembrane",
          },
        },
      ],
      observations: { mode: "never" },
      photos: { mode: "never" },
    },
    {
      itemKey: "reservoir.operationalFreeboard",
      label: "inspection.catalog.v1.items.reservoir.operationalFreeboard",
      options: YES_NO_ANOMALY_ON_NO,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
  ],
};

// ---------------------------------------------------------------------------
// Step: Extravasor / Vertedor (form §7.3.6)
// ---------------------------------------------------------------------------

const SPILLWAY_STEP: InspectionStepDef = {
  stepKey: "spillway",
  title: "inspection.catalog.v1.stepShort.spillway",
  items: [
    {
      itemKey: "spillway.type",
      label: "inspection.catalog.v1.items.spillway.type",
      descriptive: true,
      infoHints: ["informative"],
      fields: [
        {
          key: "spillwayType",
          label: "inspection.catalog.v1.common.type",
          type: "select",
          required: true,
        },
      ],
      observations: { mode: "always", required: false },
      photos: { mode: "never" },
    },
    gradeItem("spillway.inletSectionCondition", "inspection.catalog.v1.items.spillway.inletSectionCondition"),
    gradeItem(
      "spillway.outletSectionCondition",
      "inspection.catalog.v1.items.spillway.outletSectionCondition",
    ),
    gradeItem("spillway.stillingBasinCondition", "inspection.catalog.v1.items.spillway.stillingBasinCondition"),
    gradeItem("spillway.structuralCondition", "inspection.catalog.v1.common.structuralCondition"),
    {
      itemKey: "spillway.hasEmergencySpillway",
      label: "inspection.catalog.v1.items.spillway.hasEmergencySpillway",
      options: YES_NO_ANOMALY_ON_NO,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
  ],
};

// ---------------------------------------------------------------------------
// Step: Instrumentação (form §7.3.7)
// ---------------------------------------------------------------------------

const INSTRUMENTATION_STEP: InspectionStepDef = {
  stepKey: "instrumentation",
  title: "inspection.catalog.v1.stepShort.instrumentation",
  items: [
    gradeItem("instrumentation.access", "inspection.catalog.v1.items.instrumentation.access"),
    gradeItem("instrumentation.protection", "inspection.catalog.v1.items.instrumentation.protection"),
    gradeItem("instrumentation.conservationState", "inspection.catalog.v1.items.instrumentation.conservationState"),
    gradeItem(
      "instrumentation.localGaugesCondition",
      "inspection.catalog.v1.items.instrumentation.localGaugesCondition",
    ),
    gradeItem("instrumentation.identification", "inspection.catalog.v1.items.instrumentation.identification"),
    buildOthersItem("instrumentation.others"),
  ],
};

// ---------------------------------------------------------------------------
// Step: Condição de Praia (form §7.3.8) — somente informativo
// ---------------------------------------------------------------------------

const BEACH_CONDITION_STEP: InspectionStepDef = {
  stepKey: "beachCondition",
  title: "inspection.catalog.v1.stepShort.beachCondition",
  gate: {
    itemKey: "beachCondition.applies",
    label: "inspection.catalog.v1.steps.beachCondition",
    options: STEP_GATE_OPTIONS,
    observations: { mode: "never" },
    photos: { mode: "never" },
  },
  items: [
    {
      itemKey: "beachCondition.tailingsDisposal",
      label: "inspection.catalog.v1.items.beachCondition.tailingsDisposal",
      options: YES_NO_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "always", required: false },
      photos: { mode: "always", required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "beachCondition.safeWaterDistance",
      label: "inspection.catalog.v1.items.beachCondition.safeWaterDistance",
      options: YES_NO_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "always", required: false },
      photos: { mode: "always", required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "beachCondition.physicalSignaling",
      label: "inspection.catalog.v1.items.beachCondition.physicalSignaling",
      options: YES_NO_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "always", required: false },
      photos: { mode: "always", required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "beachCondition.beachLengthVsCrest",
      label: "inspection.catalog.v1.items.beachCondition.beachLengthVsCrest",
      descriptive: true,
      optional: true,
      infoHints: ["informativeNoNa", "optional"],
      fields: [
        {
          key: "lengthM",
          type: "text",
          placeholder: "inspection.catalog.v1.placeholders.beachLength",
        },
      ],
      observations: { mode: "always", required: false },
      photos: { mode: "always", required: false },
      admitsEvidence: true,
    },
    buildOthersItem("beachCondition.others"),
  ],
};

// ---------------------------------------------------------------------------
// Step: Adutoras de Rejeito / Captação de Água (form §7.3.9)
// ---------------------------------------------------------------------------

const TAILINGS_WATER_INTAKE_STEP: InspectionStepDef = {
  stepKey: "tailingsWaterIntake",
  title: "inspection.catalog.v1.stepShort.tailingsWaterIntake",
  gate: {
    itemKey: "tailingsWaterIntake.applies",
    label: "inspection.catalog.v1.steps.tailingsWaterIntake",
    options: STEP_GATE_OPTIONS,
    observations: { mode: "never" },
    photos: { mode: "never" },
  },
  items: [
    {
      itemKey: "tailingsWaterIntake.tailingsInflow",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.tailingsInflow",
      options: YES_NO_NA_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "answers", values: ["yes", "no"], required: false },
      photos: { mode: "answers", values: ["yes", "no"], required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "tailingsWaterIntake.tailingsLineLeak",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.tailingsLineLeak",
      options: YES_NO_NA_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "tailingsWaterIntake.waterPumpingActive",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.waterPumpingActive",
      options: YES_NO_NA_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "answers", values: ["yes", "no"], required: false },
      photos: { mode: "answers", values: ["yes", "no"], required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "tailingsWaterIntake.waterPipeLeak",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.waterPipeLeak",
      options: YES_NO_NA_ANOMALY_ON_YES,
      observations: { mode: "anomaly" },
      photos: { mode: "anomaly" },
      admitsEvidence: true,
    },
    {
      itemKey: "tailingsWaterIntake.intakeOperating",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.intakeOperating",
      options: YES_NO_NA_PLAIN,
      infoHints: ["optional"],
      fields: [
        {
          key: "flowRate",
          label: "inspection.catalog.v1.common.flowRate",
          type: "text",
          required: true,
          when: ["yes", "no"],
          placeholder: "inspection.catalog.v1.placeholders.flowRate",
        },
      ],
      observations: { mode: "answers", values: ["yes", "no"], required: false },
      photos: { mode: "answers", values: ["yes", "no"], required: false },
      admitsEvidence: true,
    },
    {
      itemKey: "tailingsWaterIntake.bargeAccessOperating",
      label: "inspection.catalog.v1.items.tailingsWaterIntake.bargeAccessOperating",
      options: YES_NO_NA_PLAIN,
      infoHints: ["optional"],
      observations: { mode: "answers", values: ["yes", "no"], required: false },
      photos: { mode: "answers", values: ["yes", "no"], required: false },
      admitsEvidence: true,
    },
    buildOthersItem("tailingsWaterIntake.others"),
  ],
};

export const INSPECTION_CHECKLIST_STEPS: Record<string, InspectionStepDef> = {
  accesses: ACCESSES_STEP,
  damBody: DAM_BODY_STEP,
  reservoir: RESERVOIR_STEP,
  spillway: SPILLWAY_STEP,
  instrumentation: INSTRUMENTATION_STEP,
  beachCondition: BEACH_CONDITION_STEP,
  tailingsWaterIntake: TAILINGS_WATER_INTAKE_STEP,
};

export const INSPECTION_OBSERVATIONS_LABEL_KEY = OBSERVATIONS_LABEL;
export const INSPECTION_PHOTOS_LABEL_KEY = PHOTOS_LABEL;
export const INSPECTION_GENERAL_COMMENTS_ITEM_KEY = "generalComments";
